import { useEffect, useMemo, useState } from "react";
import Button from "../atoms/button";
import { serializarTemperatura } from "./functions";

const HERDAR = "__herdar__";

export function resolveSelectedChip(modelo, recommended) {
    if (!modelo || modelo === "") return HERDAR;
    const inTop5 = (recommended || []).find((m) => m.id === modelo);
    if (inTop5) return modelo;
    return "custom";
}

const BotOpsSandboxModelPicker = ({
    catalog,
    initialModelo = "",
    initialTemperatura = "",
    saving = false,
    onSave,
}) => {
    const [modelo, setModelo] = useState(initialModelo ?? "");
    const [temperatura, setTemperatura] = useState(
        initialTemperatura == null || initialTemperatura === "" ? "" : String(initialTemperatura)
    );
    const [modalOpen, setModalOpen] = useState(false);
    const [filter, setFilter] = useState("");
    const [manualModel, setManualModel] = useState("");

    useEffect(() => {
        setModelo(initialModelo ?? "");
        setTemperatura(
            initialTemperatura == null || initialTemperatura === ""
                ? ""
                : String(initialTemperatura)
        );
    }, [initialModelo, initialTemperatura]);

    const recommended = catalog?.recommended ?? [];
    const costBenefit = catalog?.costBenefit ?? [];
    const selectedChip = resolveSelectedChip(modelo, recommended);
    const isCustom = selectedChip === "custom";

    const filteredModels = useMemo(() => {
        const allModels = catalog?.all ?? [];
        const q = filter.trim().toLowerCase();
        if (!q) return allModels;
        return allModels.filter(
            (m) =>
                m.id.toLowerCase().includes(q) ||
                (m.label && m.label.toLowerCase().includes(q)) ||
                (m.provider && m.provider.toLowerCase().includes(q))
        );
    }, [catalog, filter]);

    const chipClass = (active) =>
        `rounded-md border px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer ${
            active
                ? "border-[#d59106] bg-[#d59106] text-black"
                : "border-gray-600/60 bg-gray-900/60 text-gray-300 hover:border-[#b47c06]/60"
        }`;

    const handleSave = () => {
        onSave?.({
            modelo: modelo.trim(),
            temperatura: serializarTemperatura(temperatura),
        });
    };

    const openModal = () => {
        setFilter("");
        setManualModel(isCustom ? modelo : "");
        setModalOpen(true);
    };

    const handleModalPick = (id) => {
        setModelo(id);
        setModalOpen(false);
    };

    const handleModalManual = () => {
        const v = manualModel.trim();
        if (!v) return;
        setModelo(v);
        setModalOpen(false);
    };

    return (
        <section
            className="rounded-xl border border-[#b47c06]/40 bg-gray-900/50 p-3 space-y-3"
            data-testid="sandbox-model-picker"
        >
            <div>
                <p className="text-sm font-medium text-laranjinha">Modelo do sandbox</p>
                <p className="text-xs text-gray-400">
                    Override só no chat de teste — não altera a versão de produção.
                </p>
            </div>

            <div className="flex flex-wrap gap-2" role="group" aria-label="Modelo">
                <button
                    type="button"
                    className={chipClass(selectedChip === HERDAR)}
                    onClick={() => setModelo("")}
                >
                    Herdar produção
                </button>
                {recommended.map((m) => (
                    <button
                        key={m.id}
                        type="button"
                        className={chipClass(modelo === m.id)}
                        onClick={() => setModelo(m.id)}
                    >
                        {m.label}
                    </button>
                ))}
            </div>

            {costBenefit.length > 0 ? (
                <div data-testid="sandbox-cost-benefit-chips">
                    <p className="text-xs text-gray-500 mb-1">Custo-benefício</p>
                    <div className="flex flex-wrap gap-2" role="group" aria-label="Custo-benefício">
                        {costBenefit.map((m) => (
                            <button
                                key={m.id}
                                type="button"
                                className={chipClass(modelo === m.id)}
                                onClick={() => setModelo(m.id)}
                                data-testid={`sandbox-cost-benefit-chip-${m.id}`}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}

            <div className="flex flex-wrap gap-2" role="group" aria-label="Modelo customizado">
                <button type="button" className={chipClass(isCustom)} onClick={openModal}>
                    Outro modelo…
                </button>
            </div>

            {isCustom && modelo ? (
                <p className="text-xs text-gray-400 font-mono break-all">Selecionado: {modelo}</p>
            ) : null}

            <label className="block text-sm max-w-[160px]">
                <span className="text-laranjinha text-xs">Temperatura</span>
                <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.1}
                    value={temperatura}
                    onChange={(e) => setTemperatura(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-600/50 bg-white p-2 text-sm text-black"
                    placeholder="herdar"
                />
                <span className="block text-[10px] text-gray-500 mt-0.5">Vazio herda produção</span>
            </label>

            <Button
                click={handleSave}
                disabled={saving}
                classes="bg-[#d59106] hover:bg-[#b47c06] px-4 py-2 rounded-lg font-medium text-sm"
            >
                {saving ? "Salvando…" : "Salvar"}
            </Button>

            {modalOpen ? (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Escolher modelo"
                    data-testid="sandbox-model-modal"
                >
                    <div className="w-full max-w-lg rounded-xl border border-[#d59106]/50 bg-gray-900 p-4 shadow-xl">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-medium text-laranjinha">Outro modelo</h3>
                            <button
                                type="button"
                                onClick={() => setModalOpen(false)}
                                className="text-gray-400 hover:text-white text-sm"
                            >
                                Fechar
                            </button>
                        </div>
                        <input
                            type="search"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filtrar modelos…"
                            className="w-full mb-3 rounded-lg border border-gray-600/50 bg-white p-2 text-sm text-black"
                        />
                        <ul className="max-h-48 overflow-y-auto mb-3 space-y-1">
                            {filteredModels.map((m) => (
                                <li key={m.id}>
                                    <button
                                        type="button"
                                        className="w-full text-left rounded-md px-2 py-1.5 text-sm text-gray-200 hover:bg-gray-800"
                                        onClick={() => handleModalPick(m.id)}
                                    >
                                        <span className="font-medium">{m.label}</span>
                                        <span className="text-gray-500 text-xs ml-2 font-mono">
                                            {m.id}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <div className="border-t border-gray-700 pt-3 space-y-2">
                            <p className="text-xs text-gray-400">Ou digite fornecedor/modelo:</p>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={manualModel}
                                    onChange={(e) => setManualModel(e.target.value)}
                                    placeholder="anthropic/claude-sonnet-5"
                                    className="flex-1 rounded-lg border border-gray-600/50 bg-white p-2 text-sm text-black font-mono"
                                />
                                <Button
                                    click={handleModalManual}
                                    disabled={!manualModel.trim()}
                                    classes="bg-[#d59106] hover:bg-[#b47c06] px-3 py-2 rounded-lg text-sm"
                                >
                                    Usar
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </section>
    );
};

export default BotOpsSandboxModelPicker;
